import { useQuery } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { GroupService } from '@/effects/groups/group.service';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';
import type { GroupWithMembers } from '@/api/schemas/group.schema';
import { logger } from '@/lib/logger'

export function useUserGroups() {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_GROUPS],
    queryFn: async () => {
      logger.debug('[useUserGroups] Starting to fetch groups...');
      const program = pipe(
        GroupService,
        Effect.flatMap((service) => service.fetchUserGroups())
      );

      try {
        const result = await Effect.runPromise(
          pipe(
            program,
            Effect.provide(MainLayer)
          ) as Effect.Effect<GroupWithMembers[], never, never>
        );
        logger.debug('[useUserGroups] Fetched groups:', { groups: result });
        return result;
      } catch (error) {
        logger.error('[useUserGroups] Error fetching groups:', error);
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    networkMode: 'always'
  });
}

export function useGroupUsers(groupId: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.GROUP_USERS, groupId],
    queryFn: async () => {
      const program = pipe(
        GroupService,
        Effect.flatMap((service) => service.fetchUsersInGroup(groupId)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    networkMode: 'always'
  });
}

// Hook to get a specific group from the user's groups
export function useGroup(groupId: string) {
  const { data: groups, ...rest } = useUserGroups();

  return {
    ...rest,
    data: groups?.find((group) => group.id === groupId),
  };
}

// Hook to check if user is group owner
export function useIsGroupOwner(groupId: string, userId?: string) {
  const { data: group } = useGroup(groupId);

  return {
    isOwner: group?.owner === userId,
    group,
  };
}

// Hook to get group members count
export function useGroupMembersCount(groupId: string) {
  const { data: users, ...rest } = useGroupUsers(groupId);

  return {
    ...rest,
    count: users?.length ?? 0,
    data: users,
  };
}